import { PlusIcon } from "lucide-react";
import { useState } from "react";

import useNewItemActions from "../NewItemButton/hooks/useNewItemActions";
import { ITEM_TYPE_GROUPS, ITEM_TYPES, NewItemEntityType } from "../NewItemButton/new-item-types";
import NewItemPopoverContent from "../NewItemButton/NewItemPopoverContent";
import { useUnifiedRegistry } from "../unified-registry-context";
import { getEntityColor, getTypeIcon } from "../utils";

import SmartTableCreateDialog from "@/components/SmartTableCreateDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries/folder-entities/types";

interface CreatePlaceholderCardProps {
  variant: "folder" | "items";
  currentFolderId: number | null;
  workspaceId?: number | null;
}

const PLACEHOLDER_LABEL = "New table, prompt, or more";

const CreatePlaceholderCard = ({
  variant,
  currentFolderId,
  workspaceId,
}: CreatePlaceholderCardProps) => {
  const [open, setOpen] = useState(false);
  const store = useUnifiedRegistry();
  const { actions, isLoading, loadingType, smartTableCreateDialogProps } = useNewItemActions(
    currentFolderId ?? undefined,
    store.createInNewTab,
    workspaceId,
  );
  const { isCreateDisabled } = useRbac();

  if (variant === "folder") {
    const disabled = isCreateDisabled?.(EntityType.FOLDER);

    return (
      <>
        <SmartTableCreateDialog {...smartTableCreateDialogProps} />
        <button
          disabled={disabled}
          onClick={() => actions[EntityType.FOLDER]?.()}
          className="group flex w-full items-center gap-2.5 rounded-lg border border-dashed border-gray-200 bg-transparent px-4 py-3 text-gray-400 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50/80 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-gray-100/80 transition-colors duration-200 group-hover:bg-gray-200/60">
            <PlusIcon className="h-3 w-3" />
          </div>
          <span className="text-sm font-medium">New folder</span>
        </button>
      </>
    );
  }

  const handleItemClick = async (type: NewItemEntityType) => {
    await actions[type]?.();
    setOpen(false);
  };

  return (
    <>
      <SmartTableCreateDialog {...smartTableCreateDialogProps} />
      <Popover
        open={open}
        onOpenChange={nextOpen => {
          if (!isLoading) {
            setOpen(nextOpen);
          }
        }}
        modal={store.modal}
      >
        <PopoverTrigger asChild>
          <button
            disabled={isLoading}
            className="group flex h-full min-h-0 w-full flex-col items-center justify-center gap-2.5 overflow-hidden rounded-lg border border-dashed border-gray-200 bg-transparent text-gray-400 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50/50 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <div className="flex items-center -space-x-1">
              {ITEM_TYPES.slice(0, 5).map(item => (
                <div
                  key={item.type}
                  className={`flex h-6 w-6 items-center justify-center rounded-full ${getEntityColor(item.type)} ring-2 ring-white transition-transform group-hover:scale-110`}
                >
                  {getTypeIcon(item.type)}
                </div>
              ))}
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 ring-2 ring-white">
                <PlusIcon className="h-3 w-3 text-gray-400" />
              </div>
            </div>
            <span className="text-sm font-medium">{PLACEHOLDER_LABEL}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 rounded-xl border border-gray-200 bg-white p-2 shadow-lg"
          side="bottom"
          align="center"
          sideOffset={-40}
        >
          <NewItemPopoverContent
            groups={ITEM_TYPE_GROUPS}
            onItemClick={handleItemClick}
            isCreateDisabled={isCreateDisabled}
            isLoading={isLoading}
            loadingType={loadingType}
          />
        </PopoverContent>
      </Popover>
    </>
  );
};

export default CreatePlaceholderCard;
